// URL per resource for the pages that keep their selection in a store
// (step B1 of the redesign): Pipelines (/connect/pipelines/<id>), Logic
// Studio (/build/logic/<id>) and Agent Studio (/build/agents/<id>).
//
// Mounted once by AppShell, so those pages need no change: a link opens the
// resource, back and forward move between resources, and picking another one
// adds a history entry. It draws nothing and only re-renders itself when the
// stores change. Pages that keep their selection in local state bind it from
// inside (the ontology graph and the object type panel do).
import React from 'react';
import { usePipelineStore } from '../store/pipelineStore';
import { useLogicStore } from '../store/logicStore';
import { useAgentStore } from '../store/agentStore';
import { useResourceSelectionBinding } from './useResourceRoute';

/** Canonical id for a URL id in a loaded list (undefined while it is empty). */
function inList(list: { id: string }[], id: string): string | null | undefined {
  if (!list.length) return undefined;
  return list.some((x) => x.id === id) ? id : null;
}

const PipelinesUrl: React.FC = () => {
  const pipelines = usePipelineStore((s) => s.pipelines);
  const selectedId = usePipelineStore((s) => s.selectedPipelineId);
  useResourceSelectionBinding({
    page: 'pipelines',
    selectedId,
    // The builder shows the first pipeline when none is selected.
    select: (id) => usePipelineStore.getState().selectPipeline(id as string),
    resolve: (id) => inList(pipelines, id),
    describe: (id) => {
      const p = pipelines.find((x) => x.id === id);
      return p ? { name: p.name } : null;
    },
  });
  return null;
};

const LogicUrl: React.FC = () => {
  const functions = useLogicStore((s) => s.functions);
  const selectedId = useLogicStore((s) => s.selectedFn?.id ?? null);
  const name = useLogicStore((s) => s.selectedFn?.name ?? null);
  useResourceSelectionBinding({
    page: 'logic',
    selectedId,
    select: (id) => {
      const st = useLogicStore.getState();
      st.selectFunction(id ? st.functions.find((f) => f.id === id) ?? null : null);
    },
    resolve: (id) => inList(functions, id),
    describe: () => (name ? { name } : null),
  });
  return null;
};

const AgentsUrl: React.FC = () => {
  const agents = useAgentStore((s) => s.agents);
  const selectedId = useAgentStore((s) => s.selectedAgent?.id ?? null);
  const name = useAgentStore((s) => s.selectedAgent?.name ?? null);
  useResourceSelectionBinding({
    page: 'agents',
    selectedId,
    select: (id) => {
      const st = useAgentStore.getState();
      st.selectAgent(id ? st.agents.find((a) => a.id === id) ?? null : null);
    },
    resolve: (id) => inList(agents, id),
    describe: () => (name ? { name } : null),
  });
  return null;
};

export const ResourceUrlSync: React.FC = () => (
  <>
    <PipelinesUrl />
    <LogicUrl />
    <AgentsUrl />
  </>
);

export default ResourceUrlSync;
